import React from 'react';
import type { FC } from 'react';
import { match, lookup } from 'variant';
import type { VariantOf } from 'variant';
import { SelectInput, SelectOptionAvailability } from '@wix/cli-ui-kit';
import { useTranslation } from '@wix/cli-i18n';
import type { CreationFlow, DeveloperAppsStatus } from '../types.js';
import { useCreateAppBi } from '../bi/index.js';

type DeveloperAppsStatusExcludingNoApps = VariantOf<
  typeof DeveloperAppsStatus,
  'Available' | 'FailedLoadingApps'
>;

interface CreateAppOrExtendExistingProps {
  developerAppsStatus: DeveloperAppsStatusExcludingNoApps;
  onStartCreationFlow: (path: CreationFlow) => void;
}

export const CreateAppOrExtendExisting: FC<CreateAppOrExtendExistingProps> = ({
  developerAppsStatus,
  onStartCreationFlow,
}) => {
  const { t } = useTranslation();
  const { cliFlowStepAnswered } = useCreateAppBi();

  const exisitingAvailabilty = match(
    developerAppsStatus,
    lookup({
      FailedLoadingApps: SelectOptionAvailability.Disabled({
        reason: t('create_app.create_new_or_extend.failed_fetching_apps'),
      }),
      Available: SelectOptionAvailability.Enabled(),
    })
  );

  return (
    <>
      <SelectInput
        label={t('create_app.create_new_or_extend.label')}
        options={[
          {
            title: t('create_app.create_new_or_extend.new.title'),
            description: t('create_app.create_new_or_extend.new.description'),
            value: 'new' as const,
          },
          {
            availability: exisitingAvailabilty,
            title: t('create_app.create_new_or_extend.existing.title'),
            description: t(
              'create_app.create_new_or_extend.existing.description'
            ),
            value: 'existing' as const,
          },
        ]}
        onSubmit={({ value }) => {
          cliFlowStepAnswered({
            question: t('create_app.create_new_or_extend.label'),
            questionKey: 'create_app.create_new_or_extend.label',
            answer: value,
          });

          onStartCreationFlow(value);
        }}
      />
    </>
  );
};
